/**
 * Where the document viewer should sit, and where hover leaves it parked.
 *
 * Hovering a field card moves the viewer to the page that field was read
 * from. Resolving that as `hovered ?? activePage` made the viewer scroll
 * straight BACK when the pointer left: the viewer's programmatic scroll is
 * guarded by `isAutoScrolling`, so it never reports a page change, and
 * `activePage` still named the page the hover came from. Every hover on an
 * off-screen page bounced — page 2 on enter, page 1 on leave.
 *
 * Having the viewer report its programmatic landings instead would fix the
 * bounce and break something else: `activePage` also drives the RIGHT panel's
 * active group, and hover must not drag the panel around under the cursor.
 *
 * So hover PARKS the viewer. It travels to the hovered page and stays there
 * until the viewer is commanded explicitly — the user scrolling or paging it,
 * which is the only writer of `activePage` and clears the park.
 */
export function resolveViewerPage({
  hovered,
  parked,
  active
}: {
  /** Page of the field under the pointer right now, if any. */
  hovered: number | null;
  /** Page the last hover left the viewer on, if any. */
  parked: number | null;
  /** Page the user last scrolled or paged to. Also drives the right panel. */
  active: number;
}): number {
  return hovered ?? parked ?? active;
}

/**
 * The park after a hover change: the hovered page when there is one, else
 * whatever the last hover left behind. `??` and not `||` — page 0 parks like
 * any other page.
 */
export function nextParkedViewerPage(
  current: number | null,
  hovered: number | null
): number | null {
  return hovered ?? current;
}
